import { DEFAULT_BINGO_SETTINGS, type RoomSettings } from "@hitster/shared";
import { GameModal } from "./GameModal";
import { HintsOption } from "./HintsOption";
import { IncludeMusicGuessOption } from "./IncludeMusicGuessOption";
import { BingoSettingsForm } from "./BingoSettingsForm";

interface Props {
  open: boolean;
  starting: boolean;
  settings: RoomSettings;
  actionError?: string | null;
  onSettingsChange: (patch: Partial<RoomSettings>) => void;
  onConfirm: () => void;
  onClose: () => void;
}

export function SoloStartModal({
  open,
  starting,
  settings,
  actionError,
  onSettingsChange,
  onConfirm,
  onClose,
}: Props) {
  const gameKind = settings.gameKind ?? "timeline";

  return (
    <GameModal open={open} onClose={starting ? undefined : onClose} labelledBy="solo-start-title">
      <h2 id="solo-start-title" className="cw-title text-2xl font-bold text-left">
        Partie solo
      </h2>
      <div className="text-left space-y-4 pr-1">
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            className={`cw-btn text-sm ${gameKind === "timeline" ? "cw-btn-primary" : "cw-btn-secondary"}`}
            disabled={starting}
            onClick={() => onSettingsChange({ gameKind: "timeline" })}
          >
            🕰️ Frise
          </button>
          <button
            type="button"
            className={`cw-btn text-sm ${gameKind === "bingo" ? "cw-btn-primary" : "cw-btn-secondary"}`}
            disabled={starting}
            onClick={() =>
              onSettingsChange({
                gameKind: "bingo",
                bingo: settings.bingo ?? { ...DEFAULT_BINGO_SETTINGS },
              })
            }
          >
            🎱 Bingo
          </button>
        </div>

        {gameKind === "bingo" ? (
          <BingoSettingsForm settings={settings} onChange={onSettingsChange} />
        ) : (
          <>
            <label className="block">
              <span className="text-sm text-violet-300">Cartes pour gagner</span>
              <input
                type="number"
                inputMode="numeric"
                className="cw-input mt-1"
                min={1}
                max={50}
                value={settings.winTarget}
                disabled={starting}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  if (!Number.isFinite(n)) return;
                  onSettingsChange({ winTarget: Math.min(50, Math.max(1, Math.trunc(n))) });
                }}
              />
            </label>
            <IncludeMusicGuessOption
              checked={!!settings.includeMusicGuess}
              onChange={(includeMusicGuess) => onSettingsChange({ includeMusicGuess })}
            />
            <HintsOption settings={settings} onChange={onSettingsChange} />
          </>
        )}

        {gameKind === "bingo" && (
          <IncludeMusicGuessOption
            checked={!!settings.includeMusicGuess}
            onChange={(includeMusicGuess) => onSettingsChange({ includeMusicGuess })}
          />
        )}

        <div className="grid grid-cols-2 gap-4">
          <label className="block">
            <span className="text-sm text-violet-300">Année min (opt.)</span>
            <input
              type="number"
              className="cw-input mt-1"
              placeholder="vide = tout"
              value={settings.minYear ?? ""}
              disabled={starting}
              onChange={(e) => {
                const raw = e.target.value.trim();
                onSettingsChange({
                  minYear: raw === "" || Number.isNaN(Number(raw)) ? undefined : Number(raw),
                });
              }}
            />
          </label>
          <label className="block">
            <span className="text-sm text-violet-300">Année max (opt.)</span>
            <input
              type="number"
              className="cw-input mt-1"
              placeholder="vide = tout"
              value={settings.maxYear ?? ""}
              disabled={starting}
              onChange={(e) => {
                const raw = e.target.value.trim();
                onSettingsChange({
                  maxYear: raw === "" || Number.isNaN(Number(raw)) ? undefined : Number(raw),
                });
              }}
            />
          </label>
        </div>
      </div>
      {actionError && (
        <p className="text-sm text-red-400 text-left" role="alert">
          {actionError}
        </p>
      )}
      <div className="flex gap-3 pt-2">
        <button
          type="button"
          className="cw-btn cw-btn-secondary flex-1"
          disabled={starting}
          onClick={onClose}
        >
          Annuler
        </button>
        <button
          type="button"
          className="cw-btn cw-btn-primary flex-1"
          disabled={starting}
          onClick={onConfirm}
        >
          {starting ? "Lancement…" : "Lancer"}
        </button>
      </div>
    </GameModal>
  );
}
